/** Spoken English number words used by manufacturing dictation parse + catalog normalize. */

export const ONES: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

export const TENS: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

const SINGLE_DIGIT_ONES = Object.keys(ONES).filter((w) => (ONES[w] ?? 10) < 10);

const WORD_BY_VALUE: Record<number, string> = {};
for (const [word, n] of Object.entries(ONES)) WORD_BY_VALUE[n] = word;
for (const [word, n] of Object.entries(TENS)) WORD_BY_VALUE[n] = word;

/** 96 → "ninety six". Leaves values above 999 as digits. */
export function integerToSpokenWords(n: number): string {
  if (!Number.isInteger(n) || n < 0) return String(n);
  if (n > 999) return String(n);
  const exact = WORD_BY_VALUE[n];
  if (exact) return exact;
  if (n < 100) {
    const tens = Math.floor(n / 10) * 10;
    const ones = n % 10;
    const tensWord = WORD_BY_VALUE[tens];
    const onesWord = WORD_BY_VALUE[ones];
    if (tensWord && onesWord && ones > 0) return `${tensWord} ${onesWord}`;
    return String(n);
  }
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const hundredWord = WORD_BY_VALUE[hundreds];
  if (!hundredWord) return String(n);
  const head = `${hundredWord} hundred`;
  if (rest === 0) return head;
  return `${head} ${integerToSpokenWords(rest)}`;
}

/** ASR often writes to/for/ate next to lumber "by"/"x". */
export function applyDimensionNumberHomophones(value: string): string {
  let s = value.replace(/\btimes\b/g, "x");
  s = s.replace(/\b(?:to|too)\s+x\b/g, "2 x");
  s = s.replace(/\bx\s+(?:to|too)\b/g, "x 2");
  s = s.replace(/\b(?:for|fore)\s+x\b/g, "4 x");
  s = s.replace(/\bx\s+(?:for|fore)\b/g, "x 4");
  s = s.replace(/\bate\s+x\b/g, "8 x");
  s = s.replace(/\bx\s+ate\b/g, "x 8");
  return s;
}

/** "2x4x 9 6" (said nine six) → "2x4x96". */
export function glueSplitDimensionDigits(value: string): string {
  return value.replace(/(\d+x\d+x)\s*(\d)\s+(\d)\b/g, "$1$2$3");
}

/** Digit-canonical "2x4x96 pt" → spelled "two x four x ninety six pt". */
export function digitFormToSpokenWords(digitForm: string): string {
  let s = digitForm;
  let prev = "";
  while (s !== prev) {
    prev = s;
    s = s.replace(/(\d)x(\d)/g, "$1 x $2");
  }
  s = s.replace(/\d+/g, (m) => integerToSpokenWords(Number(m)));
  return s.replace(/\s+/g, " ").trim();
}

export function bareSpokenToken(token: string): string {
  return token.toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "");
}

/** twenty-four / ninety six → 24 / 96 before leftover single words become 20 4 / 90 6. */
export function spokenNumberWordsToDigits(value: string): string {
  let out = value;
  const tensWords = Object.keys(TENS).sort((a, b) => b.length - a.length);
  for (const ten of tensWords) {
    const tenVal = TENS[ten];
    if (tenVal == null) continue;
    for (const one of SINGLE_DIGIT_ONES) {
      const oneVal = ONES[one];
      if (oneVal == null) continue;
      out = out.replace(new RegExp(`\\b${ten}[\\s-]+${one}\\b`, "gi"), String(tenVal + oneVal));
    }
  }
  const words = [...Object.keys(ONES), ...Object.keys(TENS)].sort((a, b) => b.length - a.length);
  for (const word of words) {
    const n = ONES[word] ?? TENS[word];
    if (n == null) continue;
    out = out.replace(new RegExp(`\\b${word}s?\\b`, "gi"), String(n));
  }
  return out;
}

export function parseWordNumber(tokens: string[]): { value: number; consumed: number } | null {
  if (tokens.length === 0) return null;
  const first = bareSpokenToken(tokens[0] ?? "");
  if (!first) return null;
  if (ONES[first] != null) return { value: ONES[first] ?? 0, consumed: 1 };
  if (TENS[first] != null) {
    const next = bareSpokenToken(tokens[1] ?? "");
    if (ONES[next] != null && (ONES[next] ?? 10) < 10) {
      return { value: (TENS[first] ?? 0) + (ONES[next] ?? 0), consumed: 2 };
    }
    return { value: TENS[first] ?? 0, consumed: 1 };
  }
  const hyphen = first.split("-");
  if (hyphen.length === 2 && TENS[hyphen[0] ?? ""] != null && ONES[hyphen[1] ?? ""] != null) {
    return { value: (TENS[hyphen[0] ?? ""] ?? 0) + (ONES[hyphen[1] ?? ""] ?? 0), consumed: 1 };
  }
  return null;
}

export function parseSpokenQty(tokens: string[]): { value: number; consumed: number } | null {
  if (tokens.length === 0) return null;
  const first = bareSpokenToken(tokens[0] ?? "");
  if (/^\d+(?:\.\d+)?$/.test(first)) return { value: Number(first), consumed: 1 };
  return parseWordNumber(tokens);
}
